"use client";
import { useAPI } from "@/lib/api";
import { cn } from "@/lib/utils";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useWindowSize } from "react-use";
import { useAdsContext } from "../context/AdsContext";

const mobileDimen = 767;

const AdUnit = ({
  name = "",
  className = "",
  imgClassName = "",
  style = {},
  id = "",
  containerClassName = "",
  innerClassName = "",
  sticky = false,
}) => {
  const api = useAPI();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const ads = useAdsContext();

  const observerRef = useRef<any>(null);
  const adRef = useRef<any>(null);
  const loadedRef = useRef<any>(false);
  const initLoad = useRef<any>(false);
  const [adData, setAdData] = useState<any>(null);
  const { width: windowWidth } = useWindowSize();

  const [stickyShow, setStickyShow] = useState(false);

  const [hasLoaded, setHasLoaded] = useState(false);

  const getAdImage = useCallback(
    (item) => {
      if (windowWidth <= mobileDimen) {
        return (item?.image_url_mobile || item?.image_url) as string;
      }
      return item?.image_url as string;
    },
    [windowWidth]
  );

  useEffect(() => {
    if (sticky) {
      if (stickyShow) {
        document.body.classList.remove("__sticky-close");
        document.body.classList.add("__sticky-open");
      } else {
        document.body.classList.add("__sticky-close");
        document.body.classList.remove("__sticky-open");
      }
    }
  }, [sticky, stickyShow]);

  useEffect(() => {
    const loadAds = async () => {
      try {
        const data = await api.post("/sda/request", { name });
        setAdData(data.data);
      } catch (e) {
        console.error(e);
      }
    };
    if (ads.enabled && mounted) {
      if (!initLoad.current) {
        initLoad.current = true;
        loadAds();
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [name, ads, mounted]);

  useEffect(() => {
    if (adData && adRef.current) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      let isVisible = false;

      const checkViewability = () => {
        if (isVisible && hasLoaded) {
          trackEvent("view", adData.uuid);
          trackGA("ad_view");
          observerRef.current.disconnect();
        }
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
          checkViewability();
        });
      }, options);

      observer.observe(adRef.current);
      observerRef.current = observer;

      return () => observer.disconnect();
    }
  }, [adData, hasLoaded]);

  const trackEvent = async (eventType, adId) => {
    try {
      await api.post("/sda/tr", { e: eventType, i: adId, u: adData.id });
    } catch (e) {}
  };

  const handleImageLoad = () => {
    if (loadedRef.current) return;
    setStickyShow(true);
    loadedRef.current = true;
    trackEvent("loaded", adData.uuid);
    setHasLoaded(true);
    trackGA("ad_loaded");
  };

  const handleContentLoad = () => {
    if (loadedRef.current) return;
    setStickyShow(true);
    loadedRef.current = true;
    trackEvent("loaded", adData.uuid);
    setHasLoaded(true);
    trackGA("ad_loaded");
  };

  const trackGA = (event) => {
    (window as any).gtag("event", event, {
      unit: name,
      inv_id: adData.uuid,
      path: window.location.pathname,
    });
  };

  const executeScripts = (element) => {
    const scripts = element.getElementsByTagName("script");
    let i = 0;
    for (const script of scripts) {
      console.log("Execute script", i);
      const newScript = document.createElement("script");
      newScript.text = script.innerHTML;
      for (const attr of script.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      script.parentNode.replaceChild(newScript, script);
      i++;
    }
  };

  const renderContent = useMemo(() => {
    if (adData?.type === "image") {
      return (
        <div className={cn(sticky ? "bg-background" : "", containerClassName)}>
          <div
            className={cn(
              "relative bg-foreground/5 dark:bg-foreground/10 overflow-hidden",
              sticky ? "rounded-t-md" : "rounded-md",
              innerClassName
            )}
          >
            <div ref={adRef} className={className} id={id} style={style}>
              <a
                href={adData.click_url}
                target="_blank"
                className="relative"
                rel="nofollow"
                onClick={() => trackGA("ad_click")}
              >
                {getAdImage(adData).endsWith(".webm") ? (
                  <>
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadStart={handleImageLoad}
                      className={cn(
                        "object-contain mx-auto w-full h-full",
                        imgClassName
                      )}
                    >
                      <source src={getAdImage(adData)} type="video/webm" />
                    </video>
                  </>
                ) : (
                  <img
                    src={getAdImage(adData) as string}
                    alt="Sponsored Image"
                    className={cn(
                      "object-contain mx-auto w-full h-full",
                      imgClassName
                    )}
                    onLoad={handleImageLoad}
                  />
                )}
              </a>
            </div>
            <p className="right-1 bottom-1 absolute bg-black px-1.5 py-0.5 rounded-md text-[0.6rem] text-white leading-normal">
              Ads
            </p>
          </div>
        </div>
      );
    }

    if (adData?.type === "html") {
      return (
        <div className={cn("relative", containerClassName)}>
          <div
            className={className}
            id={id}
            style={style}
            ref={(el) => {
              if (el) {
                if (!el.innerHTML) {
                  el.innerHTML = adData.html;
                  executeScripts(el);
                  handleContentLoad();
                }
              }
            }}
          />
        </div>
      );
    }
    return <Fragment />;
  }, [
    adData,
    containerClassName,
    imgClassName,
    className,
    id,
    innerClassName,
    style,
    sticky,
  ]);

  if (!ads.enabled) return <Fragment />;

  if (adData) {
    if (sticky) {
      return mounted ? (
        createPortal(
          <div
            className={cn(
              "fixed right-0 bottom-0 left-0 z-[999]",
              stickyShow ? "block" : "hidden"
            )}
          >
            <button
              type="button"
              className="-top-6 left-3 z-[999] absolute flex justify-center items-center bg-black dark:border rounded-t-xl size-6 text-white"
              onClick={() => {
                setStickyShow(false);
                trackGA("ad_click");
              }}
            >
              <XMarkIcon className="size-4" />
            </button>
            {renderContent}
          </div>,
          document.body
        )
      ) : (
        <Fragment />
      );
    }

    return renderContent;
  }

  return <Fragment />;
};

export default AdUnit;
